// src/features/feedback/components/CreateFeedbackModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close,
  Feedback as FeedbackIcon,
  Send,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeedbackSubmit } from '../hooks/useFeedback';

export default function CreateFeedbackModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation(['feedback', 'common']);
  const { submitFeedback, submitting, error, clearError } = useFeedbackSubmit();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setFormData({ title: '', description: '' });
      setValidationErrors({});
      clearError();
    }
  }, [open, clearError]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }

    // Clear server error when user modifies form
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = t('feedback:validation.title_required', 'Title is required');
    } else if (formData.title.length > 255) {
      errors.title = t('feedback:validation.title_too_long', 'Title must be less than 255 characters');
    }

    if (!formData.description.trim()) {
      errors.description = t('feedback:validation.description_required', 'Description is required');
    } else if (formData.description.length > 5000) {
      errors.description = t('feedback:validation.description_too_long', 'Description must be less than 5000 characters');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await submitFeedback(formData);

    if (result.success) {
      // Notify parent component
      if (onSuccess) {
        onSuccess(result.data);
      }
      // Close modal
      onClose();
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleKeyPress = (event) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      handleSubmit(event);
    }
  };

  const charactersLeft = {
    title: 255 - formData.title.length,
    description: 5000 - formData.description.length,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={submitting}
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <FeedbackIcon color="primary" />
            <Box>
              <Typography variant="h6">
                {t('feedback:create.title', 'Submit Feedback')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('feedback:create.subtitle', 'Share your suggestions and ideas')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={submitting}
            edge="end"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label={t('feedback:create.title_label', 'Title')}
            placeholder={t('feedback:create.title_placeholder', 'Brief summary of your feedback')}
            variant="outlined"
            margin="normal"
            value={formData.title}
            onChange={handleInputChange('title')}
            onKeyDown={handleKeyPress}
            error={!!validationErrors.title}
            helperText={
              validationErrors.title || 
              t('feedback:create.characters_left', '{{count}} characters left', { count: charactersLeft.title })
            }
            disabled={submitting}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          <TextField
            fullWidth
            label={t('feedback:create.description_label', 'Description')}
            placeholder={t('feedback:create.description_placeholder', 'Provide detailed feedback, suggestions, or ideas...')}
            variant="outlined"
            margin="normal"
            value={formData.description}
            onChange={handleInputChange('description')}
            onKeyDown={handleKeyPress}
            error={!!validationErrors.description}
            helperText={
              validationErrors.description || 
              t('feedback:create.characters_left', '{{count}} characters left', { count: charactersLeft.description })
            }
            disabled={submitting}
            multiline
            rows={6}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              {t('feedback:create.info', 'Your feedback will be reviewed by administrators. You can track its status in the feedback page.')}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          color="inherit"
        >
          {t('common:buttons.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !formData.title.trim() || !formData.description.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <Send />}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {submitting
            ? t('feedback:create.submitting', 'Submitting...')
            : t('feedback:create.submit_button', 'Submit Feedback')
          }
        </Button>
      </DialogActions>

      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('feedback:create.keyboard_hint', 'Tip: Press Ctrl+Enter to submit')}
        </Typography>
      </Box>
    </Dialog>
  );
}